/**
 * The generic renderer.
 *
 * Nothing in this file knows what game it is drawing. It is handed a zone's
 * contents and a placement from the game's layout, and it draws cards, a pile
 * or a number accordingly. A new game reaching the client is a layout it has
 * never seen, which renders without a code change — that is the point.
 *
 * The only game-specific thing a client is ever allowed to know is which moves
 * are legal, and it does not work that out either: the server sends them.
 */

import type { ZoneView } from '../net/match.js';
import type { ZonePlacementWire } from '../net/session.js';
import { describeArg, forDisplay } from './cards.js';
import { arcOf, fanGeometry, fanHeadroom, fanHeadroomTop, fitRow } from './fan.js';
import { useWidth } from './use-width.js';

/**
 * Which row of a triangle a slot number falls in, counting from the apex.
 *
 * Deliberately re-derived here rather than imported from the game that
 * publishes it: this renderer knows no game, and the day it imports one is the
 * day adding a game means touching the client. The arithmetic is four lines
 * and the shape of a triangle is not going to change.
 */
function pyramidRowOf(slot: number): number {
  let row = 0;
  while (((row + 1) * (row + 2)) / 2 <= slot) row += 1;
  return row;
}

/** How many rows the largest surviving slot implies. */
function pyramidRows(slots: readonly number[]): number {
  let most = 0;
  for (const slot of slots) most = Math.max(most, pyramidRowOf(slot));
  return most + 1;
}
// C1: card rendering lives in exactly one module, and this is not it.
import { Card, CardBack } from './Card.js';

export interface ZoneProps {
  placement: ZonePlacementWire;
  zone: ZoneView | undefined;
  /** Cards this seat may play right now, by card id. */
  playable?: Set<string> | undefined;
  selected?: Set<string> | undefined;
  /** Cards that just arrived, marked so the new one is findable (H2). */
  fresh?: Set<string> | undefined;
  /**
   * A card that is still in the air, and so keeps its place without showing.
   *
   * One card rather than a set: only a single arrival is ever held up, and
   * several at once skip the hold entirely.
   */
  arriving?: string | undefined;
  /**
   * What a pile showed before the card now in the air was played onto it.
   *
   * A stack draws only its top card, so hiding the one still travelling would
   * leave the pile blank - the card it replaced has already gone from the view,
   * because the server sent the new top in the same packet as the move. This
   * is that card, held for as long as the flight lasts, so the pile goes on
   * looking like itself until the new card actually arrives.
   */
  beneath?: string | undefined;
  onCard?: ((card: string) => void) | undefined;
  /**
   * What tapping the PILE does, where that is a move.
   *
   * Separate from `onCard` because the thing pressed is often not a card:
   * a stock shows a back or nothing but a number, and "draw" is a move about
   * the pile rather than about any card in it. Absent when no legal move
   * starts here, which is what keeps a pile inert when it should be.
   */
  onZone?: (() => void) | undefined;
  /**
   * How many face-down cards this pile is sitting on.
   *
   * A Klondike column is a run of face-up cards on a pile of buried ones, and
   * the buried ones are drawn as slivers behind it - you cannot read them, but
   * how many there are is the whole shape of the game.
   */
  underCount?: number | undefined;
  small?: boolean | undefined;
  /**
   * Most face-down cards to fan, for a `backs` zone. Twelve by default.
   *
   * A seat on a phone ring has room for four, and the count badge beside them
   * already says the exact number — see `render/narrow.ts`.
   */
  maxBacks?: number | undefined;
  /**
   * A suit letter that overrides what this pile's top card means, or absent.
   *
   * Declared by the layout and published by the game - see `declaredSuit` in
   * GameLayout. The renderer is never the thing that decides when it applies,
   * or when it stops.
   */
  declared?: string | undefined;
  /**
   * Where each card of a `pyramid` zone sits, row-major from the apex.
   *
   * Passed in rather than read here, like `declared`: this component is handed
   * a zone and a placement and knows nothing about the view it came from.
   */
  slots?: number[] | undefined;
}

/**
 * Draw one zone.
 *
 * The shapes are the whole vocabulary, and each one answers a different
 * question: `count` is "how many", `stack` is "what is on top", `row` is "what
 * is here", `fan` is "what do I hold", `backs` is "how many are they holding".
 */
export function Zone({
  placement,
  zone,
  onZone,
  underCount,
  playable,
  selected,
  fresh,
  arriving,
  beneath,
  onCard,
  small,
  declared,
  slots: slotField,
  maxBacks,
}: ZoneProps) {
  // Measured, not guessed: how many cards share a row decides how far each one
  // tilts. Above the early return because hooks cannot be conditional.
  const [fanRef, fanWidth] = useWidth<HTMLDivElement>();
  /* The same measurement for a row that may not wrap; see the note at the row
     branch below. Above the early return, because hooks cannot be conditional. */

  if (!zone) {
    // Absent rather than empty: the viewer is not allowed to know this zone's
    // size, so drawing a zero would be a claim, and a false one.
    return null;
  }

  /* The game may ask for a small pile; a narrow seat may impose one. Either
     is enough, and neither overrides the other. */
  const drawSmall = small === true || placement.small === true;

  const cards = zone.cards ?? [];
  const count = zone.count ?? cards.length;

  /*
   * Nothing in it, and nothing to aim at: draw nothing at all.
   *
   * An empty pile used to draw a dashed rectangle wherever it was declared,
   * and on a multiplayer table that meant four or five of them scattered over
   * the felt before a card had been played - Go Fish books beside every seat,
   * Rummy melds beside every seat, Cheat's just-played, Hold'em's board. It
   * reads as a layout that has failed to load rather than as a set of places.
   *
   * A game says which of its zones are places even when empty, with `target` -
   * see the note on it in the toolkit's layout. A Klondike foundation is; a
   * pile that simply has not been used yet is not.
   */
  if (count === 0 && cards.length === 0 && placement.target !== true) return null;

  /*
   * Whether the count rides on the label line rather than under the pile.
   *
   * Only when there IS a label to ride on: the same `count` shape draws
   * somebody else's collapsed hand on a narrow seat, and those have no
   * heading - moving the number up there would delete it.
   */
  const countOnLabel =
    Boolean(placement.label) &&
    (placement.shape === 'count' || (!zone.cards && count > 0)) &&
    count > 0;
  // `describeArg` already knows the glyph, the word and the colour.
  const declaredCall = declared ? describeArg(declared) : null;

  const body = () => {
    /*
     * Checked BEFORE the count fallback below, which would otherwise swallow
     * it: somebody else's hand arrives as a count and no cards, which is
     * exactly the case this shape exists to draw.
     */
    if (placement.shape === 'backs') {
      if (count === 0) return <div className="zone-empty" aria-label="empty" />;
      /*
       * Capped, with the real number still shown.
       *
       * A seat holding thirty cards would otherwise draw thirty backs across
       * somebody's face. Past a dozen the fan has already said "a lot", and
       * the badge says exactly how many.
       */
      const drawn = Math.min(count, maxBacks ?? 12);
      return (
        <div className="zone-backs" aria-label={`${String(count)} cards`}>
          {Array.from({ length: drawn }, (_, index) => (
            <CardBack key={index} small={drawSmall} />
          ))}
          {count > drawn ? <span className="count-badge">{count}</span> : null}
        </div>
      );
    }

    if (placement.shape === 'count' || (!zone.cards && count > 0)) {
      /*
       * Nothing there is drawn as nothing, not as a back with a nought on it.
       *
       * A card back is a claim that there is a card. It began to matter when
       * narrow seats started collapsing rows to counts (D82): a showdown zone
       * nobody had shown into rendered a face-down card and a "0", which is two
       * false things about an empty space.
       */
      if (count === 0) return <div className="zone-empty" aria-label="empty" />;

      return (
        <div className="zone-count">
          <CardBack small={drawSmall} />
          {countOnLabel ? null : <span className="count-badge">{count}</span>}
        </div>
      );
    }

    if (cards.length === 0) {
      return <div className="zone-empty" aria-label="empty" />;
    }

    if (placement.shape === 'stack') {
      const top = cards[cards.length - 1] as string;

      /*
       * The card under the one still in the air, where there is one.
       *
       * `beneath` first, because a pile the server trims to its top card has
       * nothing under it to fall back on and the table remembers what was
       * there. Failing both, the pile draws its back - a pile with a card on
       * the way to it is still a pile.
       */
      const held = arriving === top;
      const shown = held ? (beneath ?? cards[cards.length - 2]) : top;

      /*
       * The top of a pile is a card you can pick up.
       *
       * This path drew it and nothing else - no `playable`, no handler - which
       * was invisible for as long as every stack was a discard nobody lifts
       * from. Klondike lifts from two: the waste, where an ace turned up is
       * the whole point of turning it, and a foundation under the take-back
       * rule. Neither could be touched.
       *
       * Only when the top card is really the top card. While one is in the air
       * `shown` is the card underneath standing in for it, and a stand-in must
       * not accept the tap meant for what is landing on it.
       */
      const live = shown !== undefined && !held;
      const canLift = live && playable?.has(shown as string) === true;

      return (
        <div className="zone-stack">
          {zone.partial || shown === undefined ? <CardBack small={drawSmall} /> : null}
          {shown === undefined ? null : (
            <Card
              card={shown}
              small={drawSmall}
              playable={canLift}
              selected={live && selected?.has(shown) === true}
              fresh={live && fresh?.has(shown) === true}
              onClick={onCard && canLift ? () => onCard(shown) : undefined}
            />
          )}
        </div>
      );
    }

    const ordered = forDisplay(cards, placement.sort);

    /*
     * A triangle, where the arrangement is a rule (Pyramid).
     *
     * `slots` says where each surviving card belongs, counted row-major from
     * the apex - the zone closes up when a card is taken, so the positions
     * travel separately. A card is playable exactly when the two resting on it
     * have gone, and that is only readable as a triangle.
     *
     * Placed by row and column rather than laid out by flex, because holes are
     * the point: the gap where a taken card was is what shows you the card
     * beneath it is now free.
     */
    /*
     * A tableau pile: overlapping downward, the last card in play (Golf).
     *
     * A `stack` would hide everything but the top, and what is buried under
     * the playable card is the whole skill here - you are choosing which
     * column to open up, which needs seeing what opening it would reach.
     *
     * Only the bottom card takes a tap, because only it is ever legal. The
     * rest are `role="img"` like any other card nobody can play, so a screen
     * reader reads the column without offering six actions that do not exist.
     */
    if (placement.shape === 'column') {
      const last = ordered.length - 1;
      return (
        <div className="zone-column">
          {/*
            The buried cards, as slivers under the run.
            
            Unreadable on purpose - they are face down - but countable, which
            is what a Klondike player is reading when they decide which pile
            to dig at.
          */}
          {Array.from({ length: underCount ?? 0 }, (_, index) => (
            <span key={`under-${String(index)}`} className="zone-buried" aria-hidden="true" />
          ))}
          {ordered.map((card, index) => (
            <Card
              key={card}
              card={card}
              small={drawSmall}
              playable={index === last && playable?.has(card) === true}
              onClick={
                index === last && onCard && playable?.has(card) ? () => onCard(card) : undefined
              }
            />
          ))}
        </div>
      );
    }

    if (placement.shape === 'pyramid') {
      const slots = slotField ?? [];
      const rows = pyramidRows(slots.length > 0 ? slots : cards.map((_, i) => i));

      return (
        <div className="zone-pyramid" style={{ '--pyramid-rows': rows } as React.CSSProperties}>
          {ordered.map((card, index) => {
            const slot = slots[index] ?? index;
            const row = pyramidRowOf(slot);
            return (
              <div
                key={card}
                className="pyramid-slot"
                style={
                  {
                    '--row': row,
                    '--col': slot - (row * (row + 1)) / 2,
                  } as React.CSSProperties
                }
              >
                <Card
                  card={card}
                  playable={playable?.has(card) === true}
                  selected={selected?.has(card) === true}
                  onClick={onCard && playable?.has(card) ? () => onCard(card) : undefined}
                />
              </div>
            );
          })}
        </div>
      );
    }


    if (placement.shape !== 'fan') {
      /*
       * A row that must stay a row shrinks its cards instead of wrapping.
       *
       * Measured off `.zone` - the same element the fan measures, and for the
       * same reason given there: its width comes from the table around it.
       * The row's own width comes from the cards INSIDE it, so sizing cards
       * from it is a loop that chases itself down. That shipped, and dealt a
       * flop, a turn and a river two pixels wide on a real table.
       *
       * The cap is the size the card would have been - a board on a wide
       * screen should not grow to fill it, because five enormous cards is not
       * what a board looks like either.
       */
      const line = placement.oneLine === true;
      const fitted = line ? fitRow(fanWidth, ordered.length) : 0;

      return (
        <div
          className={line ? 'zone-row one-line' : 'zone-row'}
          style={
            fitted > 0
              ? /* A cap, under its own name. Setting `--card-w` from
                   `var(--card-w)` is a cycle, and a cyclic custom property is
                   invalid at computed-value time - which took every dimension
                   of the card down with it. */
                ({ '--card-cap': `${String(fitted)}px` } as React.CSSProperties)
              : undefined
          }
        >
          {ordered.map((card) => (
            <Card
              key={card}
              card={card}
              small={drawSmall}
              playable={playable?.has(card) === true}
              selected={selected?.has(card) === true}
              fresh={fresh?.has(card) === true}
              arriving={arriving === card}
              onClick={onCard && playable?.has(card) ? () => onCard(card) : undefined}
            />
          ))}
        </div>
      );
    }

    /*
     * The hand, as one arc (H2).
     *
     * Both numbers come from here rather than from the stylesheet. A card's
     * width and how far apart they sit are one calculation - solving "how do
     * thirteen of these fit across a phone" gives both at once - and splitting
     * it across two files is what left the old version with a wrap point in
     * TypeScript and a card width in CSS, agreeing only by comment.
     */
    const { cardW, step, radius } = fanGeometry(ordered.length, fanWidth);

    return (
      <div
        className="zone-fan"
        style={
          {
            '--card-w': `${String(Math.round(cardW))}px`,
            // How far one card sits from the next: the sliver you actually
            // read. The stylesheet turns it into the negative margin.
            '--fan-step': `${String(Math.round(step))}px`,
            // Room for the tilt, in card-widths. A rotated card reaches past
            // the box it would otherwise occupy, and the hand is inside a
            // scroll container that clips whatever leaves its padding box.
            '--fan-headroom': fanHeadroom(ordered.length, radius),
            // Less above than below: the arc only ever drops a card.
            '--fan-headroom-top': fanHeadroomTop(ordered.length, radius),
          } as React.CSSProperties
        }
      >
        <div className="fan-row">
          {ordered.map((card, index) => (
            <Card
              key={card}
              card={card}
              small={drawSmall}
              arc={arcOf(index, ordered.length, radius)}
              playable={playable?.has(card) === true}
              selected={selected?.has(card) === true}
              fresh={fresh?.has(card) === true}
              arriving={arriving === card}
              onClick={onCard && playable?.has(card) ? () => onCard(card) : undefined}
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    /*
     * The wrapper carries the shape as a data attribute, NOT as a class.
     *
     * Using `zone-fan` on both wrapper and inner element meant the inner
     * element's `align-items: flex-end` also applied to the wrapper's column,
     * which shoved every hand into the right-hand edge of the screen.
     */
    // `data-zone` names it so something outside can find it - the table aims a
    // card in flight at the real pile by measuring this element.
    <div
      /*
       * Measured HERE, not on the fan.
       *
       * The ref used to sit on `.zone-fan`, which is centred by this element
       * and therefore shrink-wraps to its own contents - so the fan was
       * measuring a box whose width the fan itself had just set. The loop had
       * a fixed point and it was the wrong one: on a 2560px desktop the hand
       * reported a 425px container and sized itself for a phone, and on a
       * phone it settled 37px wider than the screen and got a scrollbar.
       *
       * `.zone` is stretched by `.hand-zones`, so its width comes from the
       * screen. That is the number the arithmetic wanted all along.
       */
      ref={fanRef}
      className="zone"
      data-zone={placement.zone}
      data-shape={placement.shape}
      data-one-line={placement.oneLine === true ? 'true' : undefined}
      data-region={placement.region}
      style={
        placement.row === undefined
          ? undefined
          : ({
              // Read by `.hand-zones.board`, which is the only layout that is
              // a grid. Everywhere else these are absent and nothing changes.
              '--row': placement.row,
              '--col': placement.col ?? 1,
              '--span': placement.span ?? 1,
            } as React.CSSProperties)
      }
    >
      {/*
        The heading, with the pile's size beside it.

        The number used to sit under the pile, which put it a card's height
        away from the word naming the thing it counts - and on a phone that
        pushed it into whatever sat below. On the label line the two read as
        one caption: "Draw 37".
      */}
      {placement.label ? (
        <div className="zone-head">
          {/*
            A mark where the game gave one, the word otherwise.

            The word still goes out, hidden, because a suit pip is a picture
            of a suit and "Hearts" is its name - the pip is the thing that
            does not survive being read aloud. See `shortLabel` in the
            toolkit for why the game declares this rather than the client
            recognising four English words.
          */}
          {placement.shortLabel ? (
            <span className="zone-label is-mark">
              <span aria-hidden="true">{placement.shortLabel}</span>
              <span className="sr-only">{placement.label}</span>
            </span>
          ) : (
            <span className="zone-label">{placement.label}</span>
          )}
          {countOnLabel ? <span className="count-badge">{count}</span> : null}
        </div>
      ) : null}

      <div
        className={`zone-body${onZone ? ' tappable' : ''}`}
        {...(onZone
          ? {
              role: 'button',
              tabIndex: 0,
              onClick: onZone,
              onKeyDown: (event: React.KeyboardEvent) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onZone();
                }
              },
              'aria-label': `${placement.label ?? placement.zone}: ${String(count)}`,
            }
          : {})}
      >
        {body()}

        {/*
         * The suit a wild card named, over the pile it changed the meaning of.
         *
         * Symbol AND word: hearts and diamonds are both red, so the glyph
         * alone is a coin toss at a glance and colour alone is no help to
         * anybody who cannot separate the two.
         */}
        {declaredCall ? (
          <span className={`zone-declared${declaredCall.red ? ' red' : ''}`}>
            <span className="zone-declared-mark" aria-hidden="true">
              {declaredCall.text.split(' ')[0]}
            </span>
            <span className="zone-declared-word">{declaredCall.text.split(' ')[1]}</span>
          </span>
        ) : null}
      </div>
    </div>
  );
}
